<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{ clerkId: string }>()

// Notifications endpoints are auth-gated on the Worker now (self-or-admin
// ownership) -- every call sends the caller's own Clerk session token.
const { getToken } = useClerkUser()
const emit = defineEmits<{ close: []; 'count-change': [n: number] }>()

const { apiBase: API } = useRuntimeConfig().public

interface DrawerNotif {
  id: number
  type: string
  message: string
  link?: string
  is_read: boolean
  created_at: string
}

const notifs = ref<DrawerNotif[]>([])
const loading = ref(true)

const typeLabel: Record<string, string> = {
  event_archived: 'Event Archived',
  event_assignment: 'Event Assignment',
  event_manager: 'Event Manager',
  welcome: 'Welcome',
  general: 'Notification',
  role_changed: 'Role Changed',
  link_requested: 'Link Request',
  link_confirmed: 'Link Confirmed',
  invitation: 'Invitation',
  invitation_accepted: 'Invitation Accepted',
  invitation_declined: 'Invitation Declined',
  event_approved: 'Event Approved',
  event_rejected: 'Event Rejected',
  event_complete: 'Event Complete',
}

onMounted(async () => {
  if (!props.clerkId) { loading.value = false; return }
  try {
    // clerk_id, not email -- the schema documents email as 'Display only
    // -- do not filter' on this table, and it's never reliably populated
    // by newer notification-creating code. clerk_id is a NOT NULL
    // foreign key, always populated regardless of which code created
    // the row.
    const token = await getToken()
    const data = await fetch(`${API}/manage/notifications?clerk_id=${encodeURIComponent(props.clerkId)}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(r => r.json())
    notifs.value = Array.isArray(data) ? data : []
  } catch {}
  loading.value = false
})

const unread = computed(() => notifs.value.filter(n => !n.is_read))

watch(unread, (v) => emit('count-change', v.length))

async function markRead(id: number) {
  const token = await getToken()
  await fetch(`${API}/manage/notifications/${id}/read`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } }).catch(() => {})
  notifs.value = notifs.value.map(n => n.id === id ? { ...n, is_read: true } : n)
}

const router = useRouter()

async function goToLink(n: DrawerNotif) {
  if (!n.link) return
  if (!n.is_read) await markRead(n.id)
  emit('close')
  router.push(n.link)
}

async function markAllRead() {
  const token = await getToken()
  await fetch(`${API}/manage/notifications/read-all?clerk_id=${encodeURIComponent(props.clerkId)}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } }).catch(() => {})
  notifs.value = notifs.value.map(n => ({ ...n, is_read: true }))
}

function formatDate(dt: string) {
  return new Date(dt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}
</script>

<template>
  <!-- Overlay — above nav so blur covers everything -->
  <div @click="emit('close')" style="position:fixed;inset:0;z-index:501;background:rgba(0,0,0,0.5);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);" />

  <!-- Drawer panel -->
  <Transition name="drawer" appear>
    <div style="position:fixed;top:72px;right:0;width:280px;z-index:502;background:#121D30;border-left:1px solid rgba(200,172,133,0.25);border-bottom:1px solid rgba(200,172,133,0.25);box-shadow:-8px 18px 44px rgba(0,0,0,0.55);display:flex;flex-direction:column;max-height:calc(100vh - 72px);overflow-y:auto;">

      <!-- Header -->
      <div style="padding:12px 16px;border-bottom:1px solid rgba(255,255,255,0.08);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;">
        <div style="font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:0.08em;color:#F0EBE0;">
          Notifications
          <span v-if="unread.length > 0" style="color:#C86060;font-size:14px;"> ({{ unread.length }})</span>
        </div>
        <div style="display:flex;align-items:center;gap:10px;">
          <button v-if="unread.length > 0" @click="markAllRead" style="font-size:10px;font-weight:600;color:#8A95A8;background:none;border:none;cursor:pointer;font-family:inherit;">Mark all read</button>
          <button @click="emit('close')" style="background:none;border:none;cursor:pointer;color:#8A95A8;padding:4px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>

      <!-- Notification list — scrolls internally -->
      <div style="overflow-y:auto;padding:10px 12px;display:flex;flex-direction:column;gap:8px;">
        <div v-if="loading" style="font-size:12px;color:#8A95A8;text-align:center;padding:32px 0;">Loading…</div>
        <div v-else-if="notifs.length === 0" style="font-size:12px;color:#8A95A8;text-align:center;padding:32px 0;">No notifications yet.</div>
        <div v-else v-for="n in notifs" :key="n.id" :style="`background:${n.is_read ? 'transparent' : 'rgba(200,172,133,0.08)'};border:1px solid ${n.is_read ? 'rgba(255,255,255,0.08)' : 'rgba(200,96,96,0.3)'};border-radius:6px;padding:10px 12px;opacity:${n.is_read ? 0.55 : 1};transition:opacity 0.2s;`">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
            <div style="flex:1;">
              <div style="font-size:9px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:#C8AC85;margin-bottom:4px;">
                {{ typeLabel[n.type] ?? n.type?.replace(/_/g, ' ') }}
              </div>
              <div style="font-size:12px;color:rgba(242,242,242,0.85);line-height:1.5;margin-bottom:4px;">{{ n.message }}</div>
              <div v-if="n.created_at" style="font-size:10px;color:#8A95A8;">{{ formatDate(n.created_at) }}</div>
              <button v-if="n.link" @click="goToLink(n)" style="font-size:11px;font-weight:600;color:#C86060;background:none;border:none;cursor:pointer;padding:0;margin-top:6px;text-decoration:underline;text-underline-offset:2px;font-family:inherit;">View →</button>
            </div>
            <button v-if="!n.is_read" @click="markRead(n.id)" style="font-size:9px;font-weight:600;padding:3px 8px;border-radius:3px;cursor:pointer;font-family:inherit;background:none;border:1px solid rgba(255,255,255,0.15);color:#8A95A8;flex-shrink:0;white-space:nowrap;">Mark read</button>
          </div>
        </div>
      </div>

    </div>
  </Transition>
</template>

<style>
  .drawer-enter-active,
  .drawer-leave-active {
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .drawer-enter-from,
  .drawer-leave-to {
    transform: translateX(100%);
  }
  .drawer-enter-to,
  .drawer-leave-from {
    transform: translateX(0);
  }

  .overlay-enter-active,
  .overlay-leave-active {
    transition: opacity 0.28s ease;
  }
  .overlay-enter-from,
  .overlay-leave-to {
    opacity: 0;
  }
  .overlay-enter-to,
  .overlay-leave-from {
    opacity: 1;
  }
</style>